import React from "react";
import { Instagram, Facebook } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { CONTACT } from "@/i18n/content";

const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

export default function Footer() {
  const { t } = useLang();
  const links = [
    { id: "about", label: t.nav.about },
    { id: "lessons", label: t.nav.lessons },
    { id: "schedule", label: t.nav.schedule },
    { id: "gallery", label: t.nav.gallery },
    { id: "booking", label: t.nav.book },
  ];

  return (
    <footer data-testid="site-footer" className="bg-navy text-white pt-16 md:pt-24 overflow-hidden">
      <div className="max-w-[1400px] mx-auto px-5 md:px-10">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 pb-12">
          <div className="md:col-span-5">
            <div className="font-playfair text-3xl mb-4">
              Atlas Ziz <span className="italic text-sand">Tours</span>
            </div>
            <p className="font-manrope text-white/60 max-w-sm">{t.footer.tagline}</p>
            <div className="flex items-center gap-4 mt-7">
              <a
                data-testid="footer-instagram"
                href={CONTACT.instagram}
                target="_blank"
                rel="noopener noreferrer"
                className="w-11 h-11 flex items-center justify-center text-white transition-transform hover:scale-110"
                style={{ background: "linear-gradient(45deg,#f09433,#e6683c,#dc2743,#cc2366,#bc1888)" }}
                aria-label="Instagram"
              >
                <Instagram size={19} />
              </a>
              <a
                data-testid="footer-facebook"
                href={CONTACT.facebook}
                target="_blank"
                rel="noopener noreferrer"
                className="w-11 h-11 flex items-center justify-center text-white bg-[#1877F2] transition-transform hover:scale-110"
                aria-label="Facebook"
              >
                <Facebook size={19} />
              </a>
            </div>
          </div>

          <div className="md:col-span-3 md:col-start-8">
            <p className="label-mono text-sand mb-5">{t.footer.quick}</p>
            <ul className="space-y-3">
              {links.map((l) => (
                <li key={l.id}>
                  <button
                    onClick={() => scrollTo(l.id)}
                    className="font-manrope text-white/70 hover:text-white transition-colors"
                  >
                    {l.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          <div className="md:col-span-2">
            <p className="label-mono text-sand mb-5">{t.footer.legal}</p>
            <ul className="space-y-3">
              <li><a href="#" className="font-manrope text-white/70 hover:text-white transition-colors">{t.footer.privacy}</a></li>
              <li><a href="#" className="font-manrope text-white/70 hover:text-white transition-colors">{t.footer.terms}</a></li>
              <li><a href={`mailto:${CONTACT.email}`} className="font-manrope text-white/70 hover:text-white transition-colors">{CONTACT.email}</a></li>
            </ul>
          </div>
        </div>

        <div className="pt-6 pb-8 border-t border-white/10 flex flex-col md:flex-row justify-between gap-2">
          <p className="label-mono text-white/40">© {new Date().getFullYear()} Atlas Ziz Tours</p>
          <p className="label-mono text-white/40">{t.footer.rights}</p>
        </div>
      </div>

      {/* Big typographic impact — bounded band, clipped cleanly */}
      <div className="border-t border-white/10 overflow-hidden select-none pointer-events-none">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-6 md:py-8">
          <div className="font-outfit font-bold text-sand text-[17vw] md:text-[12vw] tracking-tighter leading-[0.82] whitespace-nowrap">
            {t.footer.big}
          </div>
        </div>
      </div>
    </footer>
  );
}
